import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '@/stores/auth';
import { TotpInput, BackupCodeInput } from '@/components/auth/TotpInput';
import { LoginBackground } from '@/components/LoginBackground';
import { Shield, ArrowLeft, Key, Mail, Lock } from 'lucide-react';

interface LoginSuccessResponse {
  success: true;
  data: {
    user: {
      id: number;
      email: string;
      full_name: string;
      role: 'admin' | 'manager' | 'operator' | 'viewer';
      is_active: boolean;
      created_at: string;
      updated_at: string;
    };
    token: string;
    expires_at: string;
  };
}

interface TotpRequiredResponse {
  success: true;
  data: {
    requires_totp: true;
    temp_token: string;
    message: string;
  };
}

interface TotpVerifyResponse {
  success: true;
  data: {
    user: {
      id: number;
      email: string;
      full_name: string;
      role: 'admin' | 'manager' | 'operator' | 'viewer';
      is_active: boolean;
      created_at: string;
      updated_at: string;
    };
    token: string;
    expires_at: string;
    used_backup_code: boolean;
    remaining_backup_codes: number;
  };
}

interface ErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
  };
}

type LoginResponse = LoginSuccessResponse | TotpRequiredResponse | ErrorResponse;

// Brand Colors - Futuristic Command Center Theme
const BRAND = {
  deepBlue: '#0f172a',
  darkBlue: '#1e293b',
  cyan: '#06b6d4',
  cyanLight: '#22d3ee',
  cyanGlow: 'rgba(6, 182, 212, 0.5)',
  white: '#f8fafc',
  slate300: '#cbd5e1',
  slate400: '#94a3b8',
  slate500: '#64748b',
};

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
      staggerChildren: 0.12,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 25 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const },
  },
};

const floatingAnimation = {
  y: [0, -10, 0],
  transition: {
    duration: 5,
    ease: [0.45, 0.05, 0.55, 0.95] as const,
    repeat: Infinity,
  },
};

const glowBreathingAnimation = {
  opacity: [0.4, 0.8, 0.4],
  scale: [1, 1.1, 1],
  transition: {
    duration: 3,
    ease: [0.45, 0.05, 0.55, 0.95] as const,
    repeat: Infinity,
  },
};

// Glassmorphism Card with Floating Animation
const AnimatedGlassCard = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    className="relative w-full max-w-xs"
    initial="hidden"
    animate="visible"
    variants={containerVariants}
  >
    {/* Floating animation wrapper */}
    <motion.div
      animate={floatingAnimation}
      className="relative"
      style={{
        background: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        borderRadius: '16px',
        border: '1px solid rgba(6, 182, 212, 0.3)',
        boxShadow: `
          0 0 60px rgba(6, 182, 212, 0.15),
          0 25px 50px -12px rgba(0, 0, 0, 0.5),
          inset 0 1px 1px rgba(255, 255, 255, 0.05)
        `,
        padding: '1.5rem',
      }}
    >
      {/* Subtle inner glow */}
      <div
        className="absolute inset-0 rounded-[16px] pointer-events-none"
        style={{
          background: `linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, transparent 50%, rgba(6, 182, 212, 0.05) 100%)`,
        }}
      />
      {/* Top edge highlight */}
      <div
        className="absolute top-0 left-[10%] right-[10%] h-px pointer-events-none"
        style={{
          background: `linear-gradient(90deg, transparent, ${BRAND.cyan}, transparent)`,
          opacity: 0.5,
        }}
      />
      <div className="relative z-10">{children}</div>
    </motion.div>
  </motion.div>
);

// Static Glass Card for TOTP (no floating to avoid distraction)
const GlassCard = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    className="relative w-full max-w-md"
    initial={{ opacity: 0, scale: 0.95, y: 20 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    style={{
      background: 'rgba(15, 23, 42, 0.6)',
      backdropFilter: 'blur(24px)',
      WebkitBackdropFilter: 'blur(24px)',
      borderRadius: '24px',
      border: '1px solid rgba(6, 182, 212, 0.3)',
      boxShadow: `
        0 0 60px rgba(6, 182, 212, 0.15),
        0 25px 50px -12px rgba(0, 0, 0, 0.5),
        inset 0 1px 1px rgba(255, 255, 255, 0.05)
      `,
      padding: '2.5rem',
    }}
  >
    <div
      className="absolute inset-0 rounded-[24px] pointer-events-none"
      style={{
        background: `linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, transparent 50%, rgba(6, 182, 212, 0.05) 100%)`,
      }}
    />
    <div
      className="absolute top-0 left-[10%] right-[10%] h-px pointer-events-none"
      style={{
        background: `linear-gradient(90deg, transparent, ${BRAND.cyan}, transparent)`,
        opacity: 0.5,
      }}
    />
    <div className="relative z-10">{children}</div>
  </motion.div>
);

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showTotpInput, setShowTotpInput] = useState(false);
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [showBackupCode, setShowBackupCode] = useState(false);
  const [totpError, setTotpError] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data: LoginResponse = await response.json();

      if (!data.success) {
        throw new Error(data.error?.message || 'Invalid credentials');
      }

      // Check if TOTP is required
      if ('requires_totp' in data.data && data.data.requires_totp) {
        setTempToken(data.data.temp_token);
        setShowTotpInput(true);
        return;
      }

      // Normal login (no TOTP)
      if ('user' in data.data && 'token' in data.data) {
        login(data.data.user, data.data.token);
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid credentials';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleTotpVerify = useCallback(
    async (code: string) => {
      if (!tempToken) return;

      setError('');
      setTotpError(false);
      setLoading(true);

      try {
        const response = await fetch('/api/v1/auth/totp/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ temp_token: tempToken, code }),
        });

        const data = (await response.json()) as TotpVerifyResponse | ErrorResponse;

        if (!data.success) {
          setTotpError(true);
          throw new Error(data.error?.message || 'Invalid code');
        }

        // Show warning if backup code was used
        if (data.data.used_backup_code) {
          console.log(`Backup code used. ${data.data.remaining_backup_codes} remaining.`);
        }

        login(data.data.user, data.data.token);
        navigate('/dashboard');
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Invalid code';
        setError(message);
        setTotpError(true);
      } finally {
        setLoading(false);
      }
    },
    [tempToken, login, navigate]
  );

  const handleBackToLogin = () => {
    setShowTotpInput(false);
    setTempToken(null);
    setError('');
    setTotpError(false);
    setShowBackupCode(false);
  };

  // TOTP Verification Screen
  if (showTotpInput) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 relative">
        <LoginBackground />
        <GlassCard>
          {/* Header */}
          <div className="text-center mb-8">
            <div
              className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4"
              style={{
                background: `linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(6, 182, 212, 0.05) 100%)`,
                border: `1px solid rgba(6, 182, 212, 0.4)`,
                boxShadow: `0 0 40px rgba(6, 182, 212, 0.3)`,
              }}
            >
              <Shield className="h-10 w-10" style={{ color: BRAND.cyan }} />
            </div>
            <h2 className="text-2xl font-bold text-white">Two-Factor Authentication</h2>
            <p className="mt-2 text-slate-400">
              {showBackupCode
                ? 'Enter one of your backup codes'
                : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div
              className="rounded-lg p-4 mb-6"
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
              }}
            >
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* TOTP Input */}
          <div className="space-y-6">
            {showBackupCode ? (
              <BackupCodeInput onSubmit={handleTotpVerify} disabled={loading} />
            ) : (
              <>
                <TotpInput
                  onComplete={handleTotpVerify}
                  disabled={loading}
                  error={totpError}
                  autoFocus
                />
                {loading && <p className="text-center text-sm text-slate-400">Verifying...</p>}
              </>
            )}

            {/* Toggle backup code */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setShowBackupCode(!showBackupCode);
                  setError('');
                  setTotpError(false);
                }}
                className="inline-flex items-center gap-2 text-sm transition-colors hover:opacity-80"
                style={{ color: BRAND.cyan }}
              >
                <Key className="h-4 w-4" />
                {showBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
              </button>
            </div>

            {/* Back button */}
            <button
              type="button"
              onClick={handleBackToLogin}
              className="w-full flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-slate-300 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to login
            </button>
          </div>
        </GlassCard>
      </div>
    );
  }

  // Normal Login Screen
  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative">
      <LoginBackground />

      <AnimatedGlassCard>
        {/* Logo with glow effect */}
        <motion.div className="text-center mb-8" variants={itemVariants}>
          <div className="relative inline-block">
            {/* Breathing glow behind logo */}
            <motion.div
              className="absolute inset-0 rounded-full"
              animate={glowBreathingAnimation}
              style={{
                background: `radial-gradient(circle, rgba(6, 182, 212, 0.5) 0%, transparent 70%)`,
                filter: 'blur(25px)',
                transform: 'scale(1.8)',
              }}
            />
            <motion.img
              src="/pos-logo-white.svg"
              alt="Thermalink Logo"
              className="mx-auto h-44 w-auto relative z-10"
              style={{
                filter: `drop-shadow(0 0 20px rgba(6, 182, 212, 0.6))`,
              }}
            />
          </div>
          <motion.h2 className="mt-4 text-2xl font-bold text-white" variants={itemVariants}>
            Thermalink Control Center
          </motion.h2>
          <motion.p
            className="mt-2 font-medium"
            style={{ color: BRAND.cyan }}
            variants={itemVariants}
          >
            Chiller Command System
          </motion.p>
        </motion.div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-lg p-4"
              style={{
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.4)',
              }}
            >
              <p className="text-sm text-red-400">{error}</p>
            </motion.div>
          )}

          <motion.div className="space-y-4" variants={itemVariants}>
            {/* Email Input */}
            <motion.div variants={itemVariants}>
              <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail
                    className="h-5 w-5 transition-colors duration-300"
                    style={{ color: emailFocused ? BRAND.cyan : BRAND.slate500 }}
                  />
                </div>
                <motion.input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                  className="w-full pl-12 pr-4 py-3 rounded-lg outline-none transition-all duration-300"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: emailFocused
                      ? `2px solid ${BRAND.cyan}`
                      : '1px solid rgba(6, 182, 212, 0.2)',
                    boxShadow: emailFocused ? `0 0 25px rgba(6, 182, 212, 0.3)` : 'none',
                    color: BRAND.white,
                  }}
                  placeholder="Enter your email"
                  whileFocus={{ scale: 1.01 }}
                />
              </div>
            </motion.div>

            {/* Password Input */}
            <motion.div variants={itemVariants}>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock
                    className="h-5 w-5 transition-colors duration-300"
                    style={{ color: passwordFocused ? BRAND.cyan : BRAND.slate500 }}
                  />
                </div>
                <motion.input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  className="w-full pl-12 pr-4 py-3 rounded-lg outline-none transition-all duration-300"
                  style={{
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: passwordFocused
                      ? `2px solid ${BRAND.cyan}`
                      : '1px solid rgba(6, 182, 212, 0.2)',
                    boxShadow: passwordFocused ? `0 0 25px rgba(6, 182, 212, 0.3)` : 'none',
                    color: BRAND.white,
                  }}
                  placeholder="Enter your password"
                  whileFocus={{ scale: 1.01 }}
                />
              </div>
            </motion.div>
          </motion.div>

          {/* Sign In Button */}
          <motion.div variants={itemVariants}>
            <motion.button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-lg text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${BRAND.cyanLight} 0%, ${BRAND.cyan} 50%, ${BRAND.deepBlue} 100%)`,
                boxShadow: `0 0 40px rgba(6, 182, 212, 0.4), 0 4px 20px rgba(0, 0, 0, 0.3)`,
              }}
              whileHover={
                !loading
                  ? {
                      scale: 1.05,
                      boxShadow: `0 0 60px rgba(6, 182, 212, 0.6), 0 6px 25px rgba(0, 0, 0, 0.4)`,
                    }
                  : {}
              }
              whileTap={!loading ? { scale: 0.95 } : {}}
              transition={{ type: 'spring', stiffness: 400, damping: 17 }}
            >
              {/* Shine effect on hover */}
              <motion.div
                className="absolute inset-0 opacity-0"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
                }}
                whileHover={{ opacity: 1, x: ['0%', '100%'] }}
                transition={{ duration: 0.5 }}
              />
              {loading ? (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center justify-center gap-2 relative z-10"
                >
                  <motion.div
                    className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  />
                  Authenticating...
                </motion.span>
              ) : (
                <span className="relative z-10">Access System</span>
              )}
            </motion.button>
          </motion.div>
        </form>

        {/* Footer */}
        <motion.div variants={itemVariants} className="mt-6 text-center">
          <p className="text-xs text-slate-500">Secure Access Portal v2.0</p>
        </motion.div>
      </AnimatedGlassCard>
    </div>
  );
}
